import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { PopoverContent } from '@/new/components/shadcn/popover';

interface RelativityFieldSelectorPopoverProps {
  availableFields: FilterField[];
  localSelectedFields: Set<string>;
  setLocalSelectedFields: (fields: Set<string>) => void;
  onFieldsChange: (fields: string[]) => void;
  onClose?: () => void;
}

export function RelativityFieldSelectorPopover({
  availableFields,
  localSelectedFields,
  setLocalSelectedFields,
  onFieldsChange,
  onClose,
}: RelativityFieldSelectorPopoverProps) {

  const handleToggleField = (fieldKey: string) => {
    const newSelection = new Set(localSelectedFields);
    if (newSelection.has(fieldKey)) {
      newSelection.delete(fieldKey);
    } else {
      newSelection.add(fieldKey);
    }
    setLocalSelectedFields(newSelection);
  };

  const handleSelectAll = () => {
    setLocalSelectedFields(new Set(availableFields.map((field) => field.fieldName)));
  };

  const handleSelectNone = () => {
    setLocalSelectedFields(new Set());
  };

  const handleSave = () => {
    onFieldsChange(Array.from(localSelectedFields));
    onClose?.();
  };

  const handleCancel = () => {
    onClose?.();
  };

  const selectedCount = localSelectedFields.size;
  const allSelected = selectedCount === availableFields.length;

  return (
    <PopoverContent side="right" align="start" sideOffset={12} className="p-0">
      {/* Scrollable field list */}
      {/* Select all/clear header */}
      <div className="flex items-center justify-between border-b p-2.5 pl-4">
        <label className="flex cursor-pointer items-center gap-2.5">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => {
              if (checked) {
                handleSelectAll();
              } else {
                handleSelectNone();
              }
            }}
          />
          <span className="text-xs text-gray-500">
            <span className="font-medium text-gray-700">{selectedCount}</span> selected
          </span>
        </label>

        <Button size="sm" onClick={handleSelectNone}>
          <Icon className="lucide--x" />
          Clear all
        </Button>
      </div>

      <div className="max-h-64 space-y-1.5 overflow-y-auto px-4 py-3">
        {availableFields.map((field) => (
          <label key={field.fieldName} htmlFor={field.fieldName} className="flex cursor-pointer items-center gap-2.5">
            <Checkbox
              id={field.fieldName}
              checked={localSelectedFields.has(field.fieldName)}
              onCheckedChange={() => handleToggleField(field.fieldName)}
            />
            <span className="text-sm text-gray-700">{field.displayName}</span>
          </label>
        ))}
      </div>

      {/* Footer with Cancel/Save buttons */}
      <div className="flex w-full justify-end gap-2 border-t p-2.5">
        <Button onClick={handleCancel} variant="outline" size="sm">
          Cancel
        </Button>
        <Button onClick={handleSave} size="sm">
          <Icon className="lucide--check h-3.5 w-3.5" />
          <span>Save</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
